import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@aviary";
import { AppleSSOButton, GoogleSSOButton } from "@shared/components";
import { useFlippers } from "@shared/hooks";
import { Size } from "@shared/types/sizes";
import { FormWrapper, SignInWrapper } from "@unauthenticated/shared/components";
import { NewSSOErrorBox } from "@unauthenticated/shared/components/NewSSOErrorBox/NewSSOErrorBox";
import { useTreatmentPlanTerm } from "@unauthenticated/shared/hooks/useTreatmentPlanTerm";
import {
  PatientRxBackground,
  PatientSignUpForm,
  SignUpModalContainer,
  SignUpModalFooter,
  SignUpModalHeader,
  SignUpModalSubtitle,
  SignUpModalTitle,
  StoreLandingLayout,
  StoreLogo,
  StoreOffersPill,
} from "@unauthenticated/signupSignin/components";
import type { SubmitValues } from "@unauthenticated/signupSignin/components/PatientSignUpForm";
import { usePatientSignUpMutation } from "@unauthenticated/signupSignin/data/mutations";
import type { PatientAttributes } from "@unauthenticated/signupSignin/data/types";
import { useLogPatientSignUp } from "@unauthenticated/signupSignin/hooks";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./PatientStoreActivationPage.styles";

interface Props {
  patientAttributes: PatientAttributes;
  additionalAttributes?: any;
  csuid: string;
  pageVersion: "react_with_sso" | "react";
  newSSOErrors?: string;
}

const PatientStoreActivationPage: FC<Props> = ({
  patientAttributes,
  additionalAttributes,
  csuid,
  pageVersion,
  newSSOErrors,
}) => {
  const { t } = useTranslation();

  const [isPatientStoreLandingPageEnabled] = useFlippers("titan_patient_store_landing_page");
  const [isAppleSSO] = useFlippers("apple_sso");
  const treatmentPlanTerm = useTreatmentPlanTerm();

  const SIGN_UP_PAGE = "rx_patient_sign_up_page";

  const {
    storeLogo,
    storeName,
    firstName,
    storeSlug,
    treatmentPlanSlug,
    labRecommendationId,
    pracName,
    uuid,
  } = patientAttributes;

  const [signUp, { loading, data }] = usePatientSignUpMutation({
    onCompleted: payload => {
      if (payload.auth.patientSignUpFromRx.redirectPath) {
        window.location.assign(payload.auth.patientSignUpFromRx.redirectPath);
      }
    },
  });

  const { logField } = useLogPatientSignUp({
    eventAggregateId: csuid,
    signUpPage: SIGN_UP_PAGE,
    pageVersion,
  });

  const handleSubmit = (values: SubmitValues) => {
    const { gToken, ...attributesFromForm } = values;
    const attributes = {
      ...attributesFromForm,
      treatmentPlanSlug,
      labRecommendationId,
      storeSlug,
      uuid,
      optional: additionalAttributes,
    };
    signUp({ variables: { input: { attributes, captchaToken: gToken } } });
  };

  const signupModalSubtitle = labRecommendationId
    ? t(l.patientRxAuth.SignUpWelcomeMessageLabRecommendation, {
        firstName,
        pracName,
      })
    : t(l.patientRxAuth.SignUpWelcomeMessage, {
        firstName,
        treatmentPlanTerm,
        pracName,
      });

  const renderActivationForm = () => (
    <>
      <SignUpModalHeader>
        <StoreLogo storeLogo={storeLogo} storeName={storeName} />
        <SignUpModalTitle title={storeName} />
        {!isPatientStoreLandingPageEnabled && !labRecommendationId && (
          <StoreOffersPill patientAttributes={patientAttributes} />
        )}
        <SignUpModalSubtitle subtitle={signupModalSubtitle} />
        {isPatientStoreLandingPageEnabled && !labRecommendationId && (
          <StoreOffersPill patientAttributes={patientAttributes} />
        )}
      </SignUpModalHeader>
      <SignUpModalContainer>
        <FormWrapper>
          <SignInWrapper>
            <NewSSOErrorBox newSSOErrors={newSSOErrors} />
            <GoogleSSOButton
              isOrTextRendered={!isAppleSSO}
              storeSlug={storeSlug}
              treatmentPlanSlug={treatmentPlanSlug}
              labRecommendationId={labRecommendationId}
              signUpPage={SIGN_UP_PAGE}
              pageVersion={pageVersion}
              uuid={uuid}
            />
            {isAppleSSO && (
              <AppleSSOButton
                css={styles.appleButton}
                storeSlug={storeSlug}
                treatmentPlanSlug={treatmentPlanSlug}
                labRecommendationId={labRecommendationId}
                isOrTextRendered
                signUpPage={SIGN_UP_PAGE}
                pageVersion={pageVersion}
                uuid={uuid}
              />
            )}
            <PatientSignUpForm
              onSubmit={handleSubmit}
              isLoading={loading}
              errors={data?.auth?.patientSignUpFromRx?.errors?.fields}
              pageVersion={pageVersion}
              signUpPage={SIGN_UP_PAGE}
              logField={logField}
            />
          </SignInWrapper>
        </FormWrapper>
      </SignUpModalContainer>
    </>
  );

  const renderNewLandingPage = () => (
    <StoreLandingLayout pracName={pracName} pracAvatar={patientAttributes.pracAvatar}>
      {renderActivationForm()}
    </StoreLandingLayout>
  );

  const renderOldLandingPage = () => (
    <PatientRxBackground>
      <Modal isOpen hideClose isSize={Size.MEDIUM}>
        {renderActivationForm()}
        <SignUpModalFooter />
      </Modal>
    </PatientRxBackground>
  );

  return <>{isPatientStoreLandingPageEnabled ? renderNewLandingPage() : renderOldLandingPage()}</>;
};

export { PatientStoreActivationPage };
